import type { PainEntry } from './model';

export type Summary = {
  entryCount: number;
  averagePain: number | null;
  highestPain: number | null;
  lowPain: number | null;
  brainFogSamples: number;
  averageBrainFog: number | null;
  commonRegions: Array<{ label: string; count: number }>;
  commonPainTypes: Array<{ label: string; count: number }>;
  interventionResponses: Array<{
    label: string;
    completedCount: number;
    averageChange: number;
  }>;
  pressureSamples: number;
  highPressureAveragePain: number | null;
  lowPressureAveragePain: number | null;
};

function rounded(value: number, places = 1) {
  const scale = 10 ** places;
  return Math.round(value * scale) / scale;
}

function topCounts(values: string[], limit = 4) {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit);
}

export function entriesWithinDays(entries: PainEntry[], days: number, now = new Date()) {
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
  return entries.filter((entry) => {
    const timestamp = Date.parse(entry.occurredAtUtc);
    return Number.isFinite(timestamp) && timestamp >= cutoff && timestamp <= now.getTime() + 60_000;
  });
}

export function summarizeEntries(entries: PainEntry[]): Summary {
  if (entries.length === 0) {
    return {
      entryCount: 0,
      averagePain: null,
      highestPain: null,
      lowPain: null,
      brainFogSamples: 0,
      averageBrainFog: null,
      commonRegions: [],
      commonPainTypes: [],
      interventionResponses: [],
      pressureSamples: 0,
      highPressureAveragePain: null,
      lowPressureAveragePain: null,
    };
  }

  const painLevels = entries.map((entry) => entry.painLevel).filter((value): value is number => value !== null);
  const brainFogLevels = entries.map((entry) => entry.brainFogLevel).filter((value): value is number => value !== null);
  const completed = entries.flatMap((entry) => entry.interventions).filter(
    (intervention) => intervention.afterPain !== null,
  );
  const byIntervention = new Map<string, number[]>();
  for (const intervention of completed) {
    const changes = byIntervention.get(intervention.type) ?? [];
    changes.push(intervention.beforePain - (intervention.afterPain ?? intervention.beforePain));
    byIntervention.set(intervention.type, changes);
  }

  const pressureEntries = entries
    .filter((entry) => entry.weather.pressureHpa !== null && entry.painLevel !== null)
    .sort((a, b) => (a.weather.pressureHpa ?? 0) - (b.weather.pressureHpa ?? 0));
  const pressureMidpoint = Math.ceil(pressureEntries.length / 2);
  const lowPressure = pressureEntries.slice(0, pressureMidpoint);
  const highPressure = pressureEntries.slice(pressureMidpoint);

  const averagePain = (items: PainEntry[]) => {
    const values = items.map((entry) => entry.painLevel).filter((value): value is number => value !== null);
    return values.length ? rounded(values.reduce((sum, value) => sum + value, 0) / values.length) : null;
  };

  return {
    entryCount: entries.length,
    averagePain: painLevels.length ? rounded(painLevels.reduce((sum, value) => sum + value, 0) / painLevels.length) : null,
    highestPain: painLevels.length ? Math.max(...painLevels) : null,
    lowPain: painLevels.length ? Math.min(...painLevels) : null,
    brainFogSamples: brainFogLevels.length,
    averageBrainFog: brainFogLevels.length ? rounded(brainFogLevels.reduce((sum, value) => sum + value, 0) / brainFogLevels.length) : null,
    commonRegions: topCounts(entries.flatMap((entry) => entry.bodyRegions.map(
      (region) => region === 'Other' && entry.otherBodyRegion ? entry.otherBodyRegion : region,
    ))),
    commonPainTypes: topCounts(entries.flatMap((entry) => entry.painTypes)),
    interventionResponses: [...byIntervention.entries()]
      .map(([label, changes]) => ({
        label,
        completedCount: changes.length,
        averageChange: rounded(changes.reduce((sum, value) => sum + value, 0) / changes.length),
      }))
      .sort((a, b) => b.averageChange - a.averageChange || b.completedCount - a.completedCount),
    pressureSamples: pressureEntries.length,
    lowPressureAveragePain: averagePain(lowPressure),
    highPressureAveragePain: averagePain(highPressure),
  };
}
